import { useEffect, useState, forwardRef, useImperativeHandle, useRef } from 'react';

// Define the ref type first
interface LazyExcalidrawRef {
  excalidrawAPI: any; // Using any to avoid Excalidraw type dependencies
}

// Define the props
interface LazyExcalidrawProps {
  initialData?: {
    elements?: any[];
  };
  onChange?: (elements: any[], appState: any, files: any) => void;
  UIOptions?: {
    welcomeScreen?: boolean;
    tools?: {
      image?: boolean;
    };
  };
  viewModeEnabled?: boolean;
  theme?: string;
  autoFocus?: boolean;
  [key: string]: any;
}

// This component handles the lazy loading of Excalidraw
const LazyExcalidraw = forwardRef<LazyExcalidrawRef, LazyExcalidrawProps>((props, ref) => {
  const [ExcalidrawComponent, setExcalidrawComponent] = useState<React.ComponentType<any> | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const excalidrawRef = useRef<any>(null);
  
  // Expose the Excalidraw API via ref
  useImperativeHandle(ref, () => ({
    excalidrawAPI: excalidrawRef.current?.excalidrawAPI || null
  }), []);

  useEffect(() => {
    let isMounted = true;
    const loadExcalidraw = async () => {
      try {
        const module = await import('@excalidraw/excalidraw');
        if (isMounted) setExcalidrawComponent(() => module.Excalidraw);
      } catch (err) {
        console.error('Failed to load Excalidraw:', err);
        setError(err as Error);
      }
    };
    loadExcalidraw();
    return () => { isMounted = false; };
  }, []);

  // Show loading or error state
  if (error) {
    return (
      <div style={{ padding: '1rem', color: 'red' }}>
        Error loading Excalidraw: {error.message}
      </div>
    );
  }

  // Render the actual Excalidraw component once loaded
  if (ExcalidrawComponent) {
    return (
      <ExcalidrawComponent
        excalidrawAPI={(api) => { excalidrawRef.current = api; }}
        {...props}
        UIOptions={{
          welcomeScreen: false,
          loadingScreen: false,
          ...(props.UIOptions || {})
        }}
        theme={props.theme || 'light'}
        autoFocus={props.autoFocus !== undefined ? props.autoFocus : false}
      />
    );
  }

  // Show nothing while loading (no loading indicator)
  return null;
});

// Set display name for better debugging
LazyExcalidraw.displayName = 'LazyExcalidraw';

export { LazyExcalidraw as default, type LazyExcalidrawRef };

